import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { firstValueFrom, of } from 'rxjs';
import { catchError, filter, take, timeout } from 'rxjs/operators';
import { ADPResourceService } from 'digital-core';
import { LoggedInUserSettings } from 'src/app/core/services';
import { IPlan } from 'src/app/model/portal-profile/IPortalProfile';

const PLANS_TIMEOUT_MS = 10000;

/**
 * Redirects /plan-coverage based on the user's plan composition:
 *   - Only MAPP plans  → /plan-coverage-mapp
 *   - Only LPP plans   → /plan-coverage-single-cert-policy
 *   - Dual plans       → allow through to the plan-coverage overview
 * Fails open (returns true) if plans or CMS settings are unavailable.
 */
export const PlanCoverageRedirectGuard: CanActivateFn = async (): Promise<boolean | UrlTree> => {
  const router = inject(Router);
  const loggedInUserSettings = inject(LoggedInUserSettings);
  const resourceService = inject(ADPResourceService);

  const plans = await firstValueFrom(
    loggedInUserSettings.$plans.pipe(
      filter((p): p is IPlan[] => p !== null),
      take(1),
      timeout(PLANS_TIMEOUT_MS),
      catchError(() => of(null))
    )
  );

  if (!plans) {
    return true;
  }

  const mappDealerCodes = (resourceService.getPortalSetting('Global_MAPP_Dealer_Codes')?.Value ?? '')
    .split(',')
    .map((c) => c.trim())
    .filter(Boolean);

  if (!mappDealerCodes.length) {
    return true;
  }

  const hasMappPlan = plans.some((p) => mappDealerCodes.includes(p.dealerCode));
  const hasLppPlan = plans.some((p) => !mappDealerCodes.includes(p.dealerCode));

  if (!hasMappPlan) {
    return router.createUrlTree(['/plan-coverage-single-cert-policy']);
  }

  if (!hasLppPlan) {
    return router.createUrlTree(['/plan-coverage-mapp']);
  }

  return true;
};
